import { Box, Button, Paper, useMediaQuery, useTheme } from '@mui/material'
import { action } from 'mobx'
import { observer, useLocalObservable } from 'mobx-react'
import { useEffect, useState } from 'react'
import { focusEventConsumer } from '../../modules/FormBuilderInterop/EventBus/BuiltInEvents/FocusEvent'
import {
	FormBuilderViewer,
	StandaloneCellManager,
} from '../../modules/FormBuilderInterop/Runtimes/Standalone'
import { useModals } from '../../services/notifications/ModalService'
import {
	IValueFormBuilderCellInstance,
	ValueLikeFormBuilderCellInstance,
	isValueLikeInstance,
} from '../FormBuilderCore/cells/FormBuilderCellInstance'
import { CellType } from '../FormHost/Types/CellType'
import { IDocumentRenderer } from './IDocumentRenderer'
import { useOcrContext } from './OcrContext'
import {
	BoundingBox,
	OcrResultFieldValue,
	isBoundingBoxEmpty,
} from './OcrTypes'

type ResultsSectionProps = {
	isSubmitting: boolean
	onSubmit: (
		outcome: string,
		originalValues: Record<string, unknown>,
		obj: unknown,
	) => void
}

export const ResultsSection = observer(
	({ onSubmit, isSubmitting }: ResultsSectionProps) => {
		const ocrContext = useOcrContext()

		const theme = useTheme()
		const matches = useMediaQuery(theme.breakpoints.up('sm'))

		const [documentRenderer, setDocumentRenderer] = useState<
			IDocumentRenderer | undefined
		>(ocrContext.controller.documentRenderer)

		const [cellManager, setCellManager] = useState<StandaloneCellManager>(
			ocrContext.controller.formHost,
		)

		const store = useLocalObservable(() => ({
			originalOcrValues: {} as Record<string, unknown>,
		}))

		const modalService = useModals()

		useEffect(() => {
			setDocumentRenderer(ocrContext.controller.documentRenderer)
		}, [ocrContext.controller, ocrContext.controller.documentRenderer])

		useEffect(() => {
			setCellManager(ocrContext.controller.formHost)
		}, [ocrContext.controller, ocrContext.controller.formHost])

		useEffect(() => {
			for (const instance of cellManager.cellInstances) {
				if (instance.type !== CellType.Value) continue

				console.log(JSON.stringify(instance, null, 2))

				if (instance.value === undefined)
					throw new Error('instance must have a value for OCR')

				store.originalOcrValues[instance.name] = instance.value
			}
		}, [cellManager])

		useEffect(() => {
			if (cellManager.cellInstances.length > 0) {
				const firstCell = cellManager.cellInstances.filter(
					(v) => v.type === CellType.Value,
				)[0] as ValueLikeFormBuilderCellInstance

				if (firstCell.type !== CellType.Value) return

				const ocrValue = firstCell.value as OcrResultFieldValue

				const definition = cellManager.cellDefinitions.find(
					(v) => v.id === firstCell.definitionId,
				)

				if (definition === undefined)
					throw new Error('could not find definition for first cell instance')

				cellManager.focusCell(firstCell.id)
				ocrContext.controller.selectedBoundingBox = ocrValue.boundingBox

				console.log('ocr value!!!', JSON.stringify(ocrValue, null, 2))

				if (ocrContext.controller.mapControl.fitToPage)
					ocrContext.controller.mapControl.fitToPage(ocrValue.pageNumber - 1)

				setTimeout(() => {
					if (
						ocrContext.controller.mapControl.showBoundingBox &&
						ocrValue.boundingBox.length > 0
					)
						ocrContext.controller.mapControl.showBoundingBox(
							ocrValue.boundingBox,
							ocrValue.pageNumber,
							true,
						)
				}, 1000)
				// if (ocrContext.controller.mapControl.fitToField)
				// 	ocrContext.controller.mapControl.fitToField(definition.name)
			}
		}, [cellManager, ocrContext.controller.mapControl.showBoundingBox])

		useEffect(() => {
			const definitions = cellManager.cellDefinitions
			const instances = cellManager.cellInstances

			ocrContext.controller.resultsControl.nextField = () => {
				console.log('next field!')
				for (let i = 0; i < definitions.length - 1; i++) {
					if (
						document.activeElement === document.getElementById(instances[i].id)
					) {
						cellManager.focusCell(instances[i + 1].id)
						break
					}
				}
			}

			ocrContext.controller.resultsControl.previousField = () => {
				for (let i = definitions.length - 1; i > 0; i--) {
					if (
						document.activeElement === document.getElementById(instances[i].id)
					) {
						cellManager.focusCell(instances[i - 1].id)
						break
					}
				}
			}

			ocrContext.controller.resultsControl.nextLowConfidenceField = () => {
				for (let i = 0; i < definitions.length - 1; i++) {
					if (
						document.activeElement === document.getElementById(instances[i].id)
					) {
						for (let j = i + 1; j < definitions.length; j++) {
							if (!isValueLikeInstance(instances[j])) continue

							if (instances[j].type !== CellType.Value) continue

							const valueInstance = instances[
								j
							] as IValueFormBuilderCellInstance

							const ocrFieldValue = valueInstance.value as OcrResultFieldValue

							if (!ocrFieldValue.accepted) {
								cellManager.focusCell(instances[j].id)
								return
							}
						}
					}
				}
			}

			ocrContext.controller.resultsControl.submit = () => {
				onSubmit('Done', store.originalOcrValues, cellManager.valueObject)
			}
		}, [cellManager, ocrContext.controller, store.originalOcrValues])

		useEffect(() => {
			focusEventConsumer(
				cellManager.eventBus,
				(eventId, properties) => {
					return Promise.resolve(properties.focusType == 'FocusGained')
				},
				(eventId, props, eventSource) => {
					const instance = cellManager.cellInstances.find(
						(v) => v.id == eventSource.instanceId,
					)
					if (instance === undefined)
						throw new Error(
							'could not find instance with id ' + eventSource.instanceId,
						)
					if (instance.type === CellType.Virtual)
						throw new Error(
							'how did this even lose focus anyways??? ' +
								eventSource.instanceId,
						)

					return Promise.resolve(true)
				},
			),
				focusEventConsumer(
					cellManager.eventBus,
					(_eventId, props, source, currentSource) => {
						return Promise.resolve(
							source.definitionId === currentSource.definitionId &&
								props.focusType === 'FocusGained',
						)
					},
					action((_eventId, _props, source) => {
						const fieldDefinition =
							ocrContext.controller.formHost.cellDefinitions.find(
								(v) => v.id === source.definitionId,
							)

						const instance = ocrContext.controller.formHost.cellInstances.find(
							(v) => v.id === source.instanceId,
						)

						const mapControl = ocrContext.controller.mapControl

						if (instance?.type !== CellType.Value) {
							if (mapControl.fitToPage) mapControl.fitToPage(undefined, true)
							return Promise.resolve()
						}

						const ocrValue = instance.value as OcrResultFieldValue

						const compareBoundingBoxes = (a: BoundingBox, b: BoundingBox) => {
							if (a.length !== b.length) return false

							for (let i = 0; i < a.length; i++) {
								if (a[i].x !== b[i].x) return false
								if (a[i].y !== b[i].y) return false
							}

							return true
						}

						const foundLabelData = Object.values(
							ocrContext.controller.fields,
						).find(
							(v) =>
								v.pageNumber === ocrValue.pageNumber &&
								compareBoundingBoxes(ocrValue.boundingBox, v.boundingBox),
						)

						if (foundLabelData === undefined) {
							if (mapControl.fitToPage) mapControl.fitToPage(undefined, true)
							return Promise.resolve()
						}

						// if (
						// 	fieldDefinition === undefined ||
						// 	fieldDefinition.name === undefined
						// ) {
						// 	if (mapControl.fitToPage) mapControl.fitToPage(undefined, true)
						// 	return Promise.resolve()
						// }

						// const fieldName = fieldDefinition.name
						// const field = ocrContext.controller.fields[fieldName]

						// if (field === undefined || isBoundingBoxEmpty(field.boundingBox)) {
						// 	if (mapControl.fitToPage) mapControl.fitToPage(undefined, true)
						// 	return Promise.resolve()
						// }

						if (isBoundingBoxEmpty(foundLabelData.boundingBox)) {
							if (mapControl.fitToPage) mapControl.fitToPage(undefined, true)
							return Promise.resolve()
						}

						if (mapControl.showBoundingBox)
							mapControl.showBoundingBox(
								foundLabelData.boundingBox,
								foundLabelData.pageNumber,
								true,
							)

						return Promise.resolve()
					}),
				)

			focusEventConsumer(
				cellManager.eventBus,
				(eventId, properties) => {
					return Promise.resolve(properties.focusType == 'FocusLost')
				},
				action((eventId, props, eventSource) => {
					const instance = cellManager.cellInstances.find(
						(v) => v.id == eventSource.instanceId,
					)
					if (instance === undefined)
						throw new Error(
							'could not find instance with id ' + eventSource.instanceId,
						)
					if (!isValueLikeInstance(instance))
						throw new Error(
							'how did this even lose focus anyways??? ' +
								eventSource.instanceId,
						)

					const fieldDefinition =
						ocrContext.controller.formHost.cellDefinitions.find(
							(v) => v.id === eventSource.definitionId,
						)

					if (instance.type !== CellType.Value) return Promise.resolve(true)

					const ocrValue = instance.value as OcrResultFieldValue

					if (props.relatedTargetNodeName === 'INPUT') {
						if (
							ocrContext.controller.formHost.cellInstances.find(
								(v) => v.id === props.relatedTargetId,
							) !== undefined
						)
							return Promise.resolve(true)
					}

					if (
						ocrContext.controller.selectedBoundingBox === ocrValue.boundingBox
					)
						ocrContext.controller.selectedBoundingBox = undefined

					return Promise.resolve(true)
				}),
			)
		}, [
			cellManager,
			ocrContext.controller,
			ocrContext.controller.mapControl,
			ocrContext.controller.selectedBoundingBox,
		])

		// if (documentRenderer === undefined) {
		// 	return <FullscreenSpinner />
		// }
		return (
			<Box
				component={Paper}
				// flex={(theme) => (matches ? 8 : 5)}
				flex={5}
				width="100%"
				height="100%"
				flexDirection="column"
				display="flex"
			>
				<Box padding={2} width="100%" height="100%" overflow="scroll">
					<FormBuilderViewer cellManager={cellManager} />
				</Box>

				<Box display="flex" justifyContent="flex-end" paddingX={3}>
					<Box
						component={Button}
						fullWidth
						color="primary"
						variant="contained"
						onClick={() =>
							onSubmit('Done', store.originalOcrValues, cellManager.valueObject)
						}
						disabled={isSubmitting}
						marginY={2}
					>
						Submit
					</Box>
				</Box>
			</Box>
		)
	},
)
